import React, { useState, useCallback, useEffect } from 'react';
import SectionA from './SectionA';
import SectionB from './SectionB';
import SectionC from './SectionC';
import SectionD from './SectionD';
import SectionE from './SectionE';
import SectionF from './SectionF';
import SectionG from './SectionG';
import './StepperMain.css';
import { getExitFormByPran, IMAGE_URL, generateExitFormPDF, generateExitFormPDFWithFiles, generateExitFormPDFFinal, URL } from '../../../Api/commonAPI';
import { decryptData } from '../../../Decrypt/Decrypt';
import { encryptData } from '../../../Encrypt/Encrypt';

const StepperComponent = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 7;
  const [isLoading, setIsLoading] = useState(false);
  
  // Get user data from sessionStorage
  let user = null;
  try {
    const userString = sessionStorage.getItem("user");
    if (userString) {
      const userData = JSON.parse(userString);
      if (userData?.data) {
        user = decryptData(userData.data);
      } else {
        user = userData;
      }
    }
  } catch (error) {
    // Error parsing user data from sessionStorage
  }
  
  // Get logged in user ID
  const customerId = user?.CustomerID || user?.customerId || user?.id || user?.Id || '0';
  
  // Section A - Personal Details State
  const [sectionAData, setSectionAData] = useState({
    ExitFormID: 0,
    CustomerId: customerId,
    PranName: '',
    PranNumber: '',
    SubscriberName: '',
    OrganizationName: '',
    Subscribergendername: '',
    FatherName: '',
    MaritalStatusname: '',
    SpouseName: '',
    SpouseGendername: '',
    DOBPran: '',
    AadharNumber: '',
    PanCardNumber: '',
    MobileNo: '',
    AlternateMobileNo: '',
    EmailId: '',
    SubscriberAddress: ''
  });
  
  // Section B - Bank Details State
  const [sectionBData, setSectionBData] = useState({
    BankAccountNumber: '',
    BankName: '',
    BankBranchAddress: '',
    IFSCCode: ''
  });
  
  // Helper function to get current date in DD-MM-YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Section C - Form Data State (for auto-fill from API)
  const [sectionCFormData, setSectionCFormData] = useState({
    normalWithdrawal: 'yes',
    PRCorpusLumpsum: '',
    annuityPercentage: '',
    WorkSectorName: '',
    WorkSectorDOB: getCurrentDate(),
    NPSCorpus: '',
    WithdrawalPR: '',
    WithdrawalAmount: '',
    AnnuityValue: '',
    AnnuitySchemeName: '',
    AnnuityFrequencySchemeName: ''
  });
  
  // State to store form data from Section C (for selected bank, scheme, frequency)
  const [sectionCData, setSectionCData] = useState({
    selectedBank: null,
    annuityScheme: '',
    annuityFrequency: ''
  });

  // Section E - Nominee Details State
  const [sectionEData, setSectionEData] = useState({
    nomineeE: []
  });
  
  // Section F - Family Members State
  const [sectionFData, setSectionFData] = useState({
    member: []
  });
  
  // Section G - Documents State
  const [sectionGData, setSectionGData] = useState({
    AadharCardF: null,
    AadharCardB: null,
    PRANCard: null,
    PANCard: null,
    Passbook: null
  });
  
  // State to track Section E validation (100% nominees)
  const [isSectionEValid, setIsSectionEValid] = useState(false);
  
  // State to track Section A validation
  const [isSectionAValid, setIsSectionAValid] = useState(false);
  
  // State to track Section B validation
  const [isSectionBValid, setIsSectionBValid] = useState(false);
  
  // State to track Section C validation
  const [isSectionCValid, setIsSectionCValid] = useState(false);

  // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
  const convertYYYYMMDDToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    // Handle YYYY-MM-DD format
    if (dateString.includes('-') && dateString.length === 10) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return dateString;
  };

  // Sync DOBPran from SectionA to WorkSectorDOB in SectionC
  useEffect(() => {
    if (sectionAData.DOBPran) {
      const convertedDate = convertYYYYMMDDToDDMMYYYY(sectionAData.DOBPran);
      // Only update if the date is different to prevent unnecessary updates
      setSectionCFormData(prev => {
        if (prev.WorkSectorDOB !== convertedDate) {
          return { ...prev, WorkSectorDOB: convertedDate };
        }
        return prev;
      });
    }
  }, [sectionAData.DOBPran]);

  // Memoized callback for SectionC data changes to prevent infinite loops
  const handleSectionCDataChange = useCallback((data) => {
    // Update form data if it contains form fields
    if (data.PRCorpusLumpsum !== undefined || data.WorkSectorName !== undefined || data.NPSCorpus !== undefined || data.WorkSectorDOB !== undefined) {
      setSectionCFormData(prev => ({ ...prev, ...data }));
    }
    // Update selected bank, scheme, frequency
    if (data.selectedBank !== undefined || data.annuityScheme !== undefined || data.annuityFrequency !== undefined) {
      setSectionCData(prev => ({ ...prev, ...data }));
    }
  }, []); // Empty dependency array since we're using functional updates

  const steps = [
    { number: 1, title: 'Section A', subtitle: 'Personal Details' },
    { number: 2, title: 'Section B', subtitle: 'Bank Details' },
    { number: 3, title: 'Section C', subtitle: 'Withdrawal Details' },
    { number: 4, title: 'Section D', subtitle: 'Annuity Details' },
    { number: 5, title: 'Section E', subtitle: 'Nomination Details' },
    { number: 6, title: 'Section F', subtitle: 'Family Members' },
    { number: 7, title: 'Section G', subtitle: 'Documents' }
  ];

  // Helper: Section A always, Section B included from step 2 onward
  const buildPayloadForStep = (step) => {
    const payload = {
      ExitFormID: sectionAData.ExitFormID || 0,
      SubscriberId: null,
      ReferenceCode: null,
      SubscriberName: sectionAData.SubscriberName || '',
      OrganizationName: sectionAData.OrganizationName || '',
      PranNumber: sectionAData.PranNumber || '',
      PranName: sectionAData.PranName || '',
      SubscriberGenderId: null,
      SubscriberGendername: sectionAData.Subscribergendername || '',
      FatherName: sectionAData.FatherName || '',
      MaritalStatusId: null,
      MaritalStatusname: sectionAData.MaritalStatusname || '',
      SpouseName: sectionAData.SpouseName || '',
      SpouseGenderId: null,
      SpouseGendername: sectionAData.SpouseGendername || '',
      DateofExit: null,
      DOBPran: sectionAData.DOBPran ? (sectionAData.DOBPran.includes('T') ? sectionAData.DOBPran : `${sectionAData.DOBPran}T00:00:00`) : null,
      AadharNumber: sectionAData.AadharNumber || '',
      PanCardNumber: sectionAData.PanCardNumber || '',
      MobileNo: sectionAData.MobileNo || '',
      AlternateMobileNo: sectionAData.AlternateMobileNo || '',
      EmailId: sectionAData.EmailId || '',
      SubscriberAddress: sectionAData.SubscriberAddress || '',
      CustomerID: sectionAData.CustomerId || customerId || 0
    };

    if (step >= 2) {
      payload.BankAccountNumber = sectionBData.BankAccountNumber || '';
      payload.BankName = sectionBData.BankName || '';
      payload.BankBranchAddress = sectionBData.BankBranchAddress || '';
      payload.IFSCCode = sectionBData.IFSCCode || '';
    }

    if (step >= 3) {
      payload.PRCorpusLumpsum = sectionCFormData.PRCorpusLumpsum || '';
      payload.WorkSectorName = sectionCFormData.WorkSectorName || '';
      // Convert WorkSectorDOB from DD-MM-YYYY to YYYY-MM-DD format if needed
      if (sectionCFormData.WorkSectorDOB) {
        if (sectionCFormData.WorkSectorDOB.includes('T')) {
          payload.WorkSectorDOB = sectionCFormData.WorkSectorDOB;
        } else {
          // Convert DD-MM-YYYY to YYYY-MM-DD format
          const parts = sectionCFormData.WorkSectorDOB.split('-');
          if (parts.length === 3) {
            payload.WorkSectorDOB = `${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`;
          } else {
            payload.WorkSectorDOB = sectionCFormData.WorkSectorDOB;
          }
        }
      } else {
        payload.WorkSectorDOB = null;
      }
      payload.NPSCorpus = sectionCFormData.NPSCorpus || '';
      payload.WithdrawalPR = sectionCFormData.WithdrawalPR || '';
      payload.WithdrawalAmount = sectionCFormData.WithdrawalAmount || '';
      payload.AnnuityValue = sectionCFormData.AnnuityValue || '';
      payload.AnnuitySchemeName = sectionCFormData.AnnuitySchemeName || '';
      payload.AnnuityFrequencySchemeName = sectionCFormData.AnnuityFrequencySchemeName || '';
      
      // Add selected bank data if available
      if (sectionCData.selectedBank) {
        payload.BankMasterID = sectionCData.selectedBank.BankMasterID || sectionCData.selectedBank.BankID || null;
      }
    }

    if (step >= 5) {
      // Add Section E nominee data
      if (sectionEData.nomineeE && Array.isArray(sectionEData.nomineeE) && sectionEData.nomineeE.length > 0) {
        payload.nomineeE = sectionEData.nomineeE.map(nominee => {
          // Convert DateOfBirth format if needed
          let dateOfBirth = nominee.DateOfBirth || '';
          if (dateOfBirth && !dateOfBirth.includes('T')) {
            // Check if it's in DD-MM-YYYY format
            const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
            if (ddmmyyyyPattern.test(dateOfBirth)) {
              const parts = dateOfBirth.split('-');
              dateOfBirth = `${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`;
            } else {
              dateOfBirth = `${dateOfBirth}T00:00:00`;
            }
          }

          // Include all parameters from nominee object - preserve all existing fields
          const nomineePayload = {
            NomineeID: nominee.NomineeID || null,
            ExitFormID: nominee.ExitFormID || null,
            FirstName: nominee.FirstName || '',
            MiddleName: nominee.MiddleName || null,
            LastName: nominee.LastName || null,
            RelationshipWthNomineeID: nominee.RelationshipWthNomineeID || null,
            RelationshipWthNomineeName: nominee.RelationshipWthNomineeName || '',
            DateOfBirth: dateOfBirth || null,
            G_FirstName: nominee.G_FirstName || '',
            G_MiddleName: nominee.G_MiddleName || '',
            G_LastName: nominee.G_LastName || '',
            NomineePercentage: nominee.NomineePercentage || '',
            CreatedBy: nominee.CreatedBy || null,
            CreatedDate: nominee.CreatedDate || null,
            UpdatedBy: nominee.UpdatedBy || null,
            UpdatedDate: nominee.UpdatedDate || null
          };

          // Include any additional fields that might exist in nominee object (except IsNominee)
          Object.keys(nominee).forEach(key => {
            if (!nomineePayload.hasOwnProperty(key) && key !== 'IsNominee') {
              nomineePayload[key] = nominee[key];
            }
          });

          return nomineePayload;
        });
        // Add IsNominee at payload level (outside the array)
        payload.IsNominee = true;
        // Add IsSave at payload level for Section E
        payload.IsSave = true;
      }
    }

    if (step >= 6) {
      // Add Section F family members data
      if (sectionFData.member && Array.isArray(sectionFData.member) && sectionFData.member.length > 0) {
        payload.member = sectionFData.member.map(member => {
          // Convert M_DateOfBirth format if needed
          let dateOfBirth = member.M_DateOfBirth || '';
          if (dateOfBirth && !dateOfBirth.includes('T')) {
            // Check if it's in DD-MM-YYYY format
            const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
            if (ddmmyyyyPattern.test(dateOfBirth)) {
              const parts = dateOfBirth.split('-');
              dateOfBirth = `${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`;
            } else {
              dateOfBirth = `${dateOfBirth}T00:00:00`;
            }
          }

          // Exclude IsMember from individual member objects
          const memberPayload = {
            RelationshipSubscriberName: member.RelationshipSubscriberName || '',
            M_FirstName: member.M_FirstName || '',
            M_LastName: member.M_LastName || '',
            M_MiddleName: member.M_MiddleName || '',
            AadharNumber: member.AadharNumber || '',
            PanCardNumber: member.PanCardNumber || '',
            M_DateOfBirth: dateOfBirth || null
          };

          // Include any additional fields that might exist in member object (except IsMember)
          Object.keys(member).forEach(key => {
            if (!memberPayload.hasOwnProperty(key) && key !== 'IsMember') {
              memberPayload[key] = member[key];
            }
          });

          return memberPayload;
        });
        // Add IsMember at payload level (outside the array)
        payload.IsMember = true;
      }
    }

    if (step >= 7) {
      // Add Section G documents data
      // Only include string URLs (from API), not File objects (new uploads)
      // File objects will be handled separately in FormData
      payload.AadharCardF = (sectionGData.AadharCardF instanceof File) ? null : (sectionGData.AadharCardF || null);
      payload.AadharCardB = (sectionGData.AadharCardB instanceof File) ? null : (sectionGData.AadharCardB || null);
      payload.PRANCard = (sectionGData.PRANCard instanceof File) ? null : (sectionGData.PRANCard || null);
      payload.PANCard = (sectionGData.PANCard instanceof File) ? null : (sectionGData.PANCard || null);
      payload.Passbook = (sectionGData.Passbook instanceof File) ? null : (sectionGData.Passbook || null);
    }

    return payload;
  };

  const handleNext = async () => {
    if (currentStep < totalSteps) {
      const payload = buildPayloadForStep(currentStep);
      
      // Call API for all sections A to G (steps 1-7)
      setIsLoading(true);
      try {
        const response = await generateExitFormPDF(payload);
      } catch (error) {
        // Error calling AddorUpdateExitForm_Web API
      } finally {
        setIsLoading(false);
      }

      setCurrentStep(currentStep + 1);
    }
  };

  // Wrapper function to handle close
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    // Build payload for Section G (final step)
    const payload = buildPayloadForStep(currentStep);
    
    // Check if there are any File objects in the documents
    const hasFiles = sectionGData.AadharCardF instanceof File ||
                     sectionGData.AadharCardB instanceof File ||
                     sectionGData.PRANCard instanceof File ||
                     sectionGData.PANCard instanceof File ||
                     sectionGData.Passbook instanceof File;
    
    // Prepare complete payload for console log
    const completePayload = { ...payload };
    const uploadedFiles = [];
    
    setIsLoading(true);
    
    // If files exist, use FormData with files and ExitFormID
    if (hasFiles) {
      try {
        const formData = new FormData();
        
        // Add ExitFormID as plain value (no encryption)
        if (payload.ExitFormID) {
          formData.append('ExitFormID', payload.ExitFormID);
        }
        
        // Add files as separate parameters in FormData
        if (sectionGData.AadharCardF instanceof File) {
          formData.append('AadharCardF', sectionGData.AadharCardF);
          uploadedFiles.push({ field: 'AadharCardF', name: sectionGData.AadharCardF.name, size: `${(sectionGData.AadharCardF.size / 1024).toFixed(2)} KB` });
          completePayload.AadharCardF = `[File: ${sectionGData.AadharCardF.name} - ${(sectionGData.AadharCardF.size / 1024).toFixed(2)} KB]`;
        }
        if (sectionGData.AadharCardB instanceof File) {
          formData.append('AadharCardB', sectionGData.AadharCardB);
          uploadedFiles.push({ field: 'AadharCardB', name: sectionGData.AadharCardB.name, size: `${(sectionGData.AadharCardB.size / 1024).toFixed(2)} KB` });
          completePayload.AadharCardB = `[File: ${sectionGData.AadharCardB.name} - ${(sectionGData.AadharCardB.size / 1024).toFixed(2)} KB]`;
        }
        if (sectionGData.PRANCard instanceof File) {
          formData.append('PRANCard', sectionGData.PRANCard);
          uploadedFiles.push({ field: 'PRANCard', name: sectionGData.PRANCard.name, size: `${(sectionGData.PRANCard.size / 1024).toFixed(2)} KB` });
          completePayload.PRANCard = `[File: ${sectionGData.PRANCard.name} - ${(sectionGData.PRANCard.size / 1024).toFixed(2)} KB]`;
        }
        if (sectionGData.PANCard instanceof File) {
          formData.append('PANCard', sectionGData.PANCard);
          uploadedFiles.push({ field: 'PANCard', name: sectionGData.PANCard.name, size: `${(sectionGData.PANCard.size / 1024).toFixed(2)} KB` });
          completePayload.PANCard = `[File: ${sectionGData.PANCard.name} - ${(sectionGData.PANCard.size / 1024).toFixed(2)} KB]`;
        }
        if (sectionGData.Passbook instanceof File) {
          formData.append('Passbook', sectionGData.Passbook);
          uploadedFiles.push({ field: 'Passbook', name: sectionGData.Passbook.name, size: `${(sectionGData.Passbook.size / 1024).toFixed(2)} KB` });
          completePayload.Passbook = `[File: ${sectionGData.Passbook.name} - ${(sectionGData.Passbook.size / 1024).toFixed(2)} KB]`;
        }
        
        // Call API with FormData using new function
        const response = await generateExitFormPDFWithFiles(formData);
        
        // Call final GenerateExitFormPDF API after successful file upload
          try {
            const pdfResponse = await generateExitFormPDFFinal(payload.ExitFormID);
            console.log('=== GenerateExitFormPDF API Response ===', pdfResponse);
          } catch (pdfError) {
            // Error calling GenerateExitFormPDF API
          }
        
        if (onComplete) {
          onComplete();
        }
        onClose();
      } catch (error) {
        // Error calling AddorUpdateExitForm_Web API
        if (onComplete) {
          onComplete();
        }
        onClose();
      } finally {
        setIsLoading(false);
      }
    } else {
      // No files, use regular JSON payload (same as other sections)
      try {
        const response = await generateExitFormPDF(payload);
        
        // Call final GenerateExitFormPDF API
        if (payload.ExitFormID) {
          try {
            const pdfResponse = await generateExitFormPDFFinal(payload.ExitFormID);
            const response = decryptData(pdfResponse?.data.data)
            const pdfUrl = `${IMAGE_URL}/${response}`;
            console.log("response",response,pdfUrl)
            if (pdfUrl) {
               window.open(pdfUrl, '_blank');
              }
            console.log('=== GenerateExitFormPDF API Response ===', response);
          } catch (pdfError) {
            // Error calling GenerateExitFormPDF API
          }
        }
        
        if (onComplete) {
          onComplete();
        }
        onClose();
      } catch (error) {
        // Error calling AddorUpdateExitForm_Web API
        if (onComplete) {
          onComplete();
        }
        onClose();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    return 'pending';
  };

  // Helper function to convert date format from API to input format
  const convertDateToInputFormat = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle ISO date format: "2003-09-01T00:00:00" -> "2003-09-01"
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      return dateString;
    } catch (error) {
      return '';
    }
  };

  // Handle PRAN Number API Call when 12 digits are entered
  const handlePranNumberComplete = async (pranNumber) => {
    setIsLoading(true);
    try {
      const response = await getExitFormByPran(pranNumber, customerId);
      if (response?.data?.data) {
        const decryptedData = decryptData(response.data.data);
        if (decryptedData && decryptedData?.ExitFormID) {
            const apiData = decryptedData;
          setSectionAData(prev => ({
            ...prev,
            ExitFormID: apiData.ExitFormID || 0,
            PranNumber: apiData.PranNumber || pranNumber,
            PranName: (apiData.PranName || '').trim(),
            SubscriberName: (apiData.SubscriberName || '').trim(),
            OrganizationName: (apiData.OrganizationName || '').trim(),
            Subscribergendername: apiData.Subscribergendername || apiData.SubscriberGendername || '',
            FatherName: (apiData.FatherName || '').trim(),
            MaritalStatusname: apiData.MaritalStatusname || '',
            SpouseName: (apiData.SpouseName || '').trim(),
            SpouseGendername: apiData.SpouseGendername || '',
            DOBPran: convertDateToInputFormat(apiData.DOBPran),
            AadharNumber: apiData.AadharNumber || '',
            PanCardNumber: apiData.PanCardNumber || '',
            MobileNo: apiData.MobileNo || '',
            AlternateMobileNo: apiData.AlternateMobileNo || '',
            EmailId: apiData.EmailId || '',
            SubscriberAddress: (apiData.SubscriberAddress || '').trim()
          }));
          
          // Update sectionBData with API response
          setSectionBData(prev => ({
            ...prev,
            BankAccountNumber: (apiData.BankAccountNumber || '').trim(),
            BankName: (apiData.BankName || '').trim(),
            BankBranchAddress: (apiData.BankBranchAddress || '').trim(),
            IFSCCode: (apiData.IFSCCode || '').trim()
          }));
          
          // Update sectionCFormData with API response
          setSectionCFormData(prev => ({
            ...prev,
            PRCorpusLumpsum: apiData.PRCorpusLumpsum || apiData.LumpSumPercentage || '',
            WorkSectorName: (apiData.WorkSectorName || apiData.WorkSector || '').trim(),
            WorkSectorDOB: convertDateToInputFormat(apiData.WorkSectorDOB || apiData.SubscriberDateOfBirth || ''),
            NPSCorpus: apiData.NPSCorpus || apiData.NPSCorpusAmount || '',
            WithdrawalPR: apiData.WithdrawalPR || apiData.WithdrawalPercentage || '',
            WithdrawalAmount: apiData.WithdrawalAmount || '',
            AnnuityValue: apiData.AnnuityValue || '',
            AnnuitySchemeName: (apiData.AnnuitySchemeName || apiData.AnnuityScheme || '').trim(),
            AnnuityFrequencySchemeName: (apiData.AnnuityFrequencySchemeName || apiData.AnnuityFrequency || '').trim()
          }));
          
          // Update sectionEData with API response (nomineeE array)
          if (apiData.nomineeE && Array.isArray(apiData.nomineeE)) {
            const processedNominees = apiData.nomineeE.length > 0
              ? apiData.nomineeE.map(nominee => ({
                  // Include all parameters from API response - preserve all fields for edit
                  NomineeID: nominee.NomineeID || null,
                  ExitFormID: nominee.ExitFormID || null,
                  FirstName: (nominee.FirstName || '').trim(),
                  MiddleName: nominee.MiddleName || null,
                  LastName: nominee.LastName || null,
                  RelationshipWthNomineeID: nominee.RelationshipWthNomineeID || null,
                  RelationshipWthNomineeName: (nominee.RelationshipWthNomineeName || '').trim(),
                  DateOfBirth: convertDateToInputFormat(nominee.DateOfBirth || ''),
                  G_FirstName: (nominee.G_FirstName || '').trim(),
                  G_MiddleName: (nominee.G_MiddleName || '').trim(),
                  G_LastName: (nominee.G_LastName || '').trim(),
                  NomineePercentage: nominee.NomineePercentage || '',
                  IsNominee: true,  // Added this line to mark the nominee as true
                  // Include any additional fields from API
                  ...Object.keys(nominee).reduce((acc, key) => {
                    if (!['NomineeID', 'ExitFormID', 'FirstName', 'MiddleName', 'LastName', 
                           'RelationshipWthNomineeID', 'RelationshipWthNomineeName', 'DateOfBirth',
                           'G_FirstName', 'G_MiddleName', 'G_LastName', 'NomineePercentage',
                           'CreatedBy', 'CreatedDate', 'UpdatedBy', 'UpdatedDate', 'IsNominee'].includes(key)) {  // Added 'IsNominee' to the list of excluded keys
                      acc[key] = nominee[key];
                    }
                    return acc;
                  }, {})
                }))
              : [];
                
            setSectionEData({
              nomineeE: processedNominees
            });
          } else {
            // Initialize empty array if nomineeE doesn't exist
            setSectionEData({
              nomineeE: []
            });
          }
          
          // Update sectionFData with API response (member or member array)
          const memberArray = apiData.member || apiData.member;
          if (memberArray && Array.isArray(memberArray)) {
            const processedMembers = memberArray.length > 0
              ? memberArray.map(member => ({
                  RelationshipSubscriberName: (member.RelationshipSubscriberName || '').trim(),
                  M_FirstName: (member.M_FirstName || '').trim(),
                  M_LastName: (member.M_LastName || '').trim(),
                  M_MiddleName: (member.M_MiddleName || '').trim(),
                  AadharNumber: (member.AadharNumber || '').trim(),
                  PanCardNumber: (member.PanCardNumber || '').trim(),
                  M_DateOfBirth: convertDateToInputFormat(member.M_DateOfBirth || '')
                }))
              : [];
                 
            setSectionFData({
              member: processedMembers
            });
          } else {
            // Initialize empty array if member/member doesn't exist
            setSectionFData({
              member: []
            });
          }
          
          // Update sectionGData with API response (document URLs)
          setSectionGData({
            AadharCardF: apiData.AadharCardF || null,
            AadharCardB: apiData.AadharCardB || null,
            PRANCard: apiData.PRANCard || null,
            PANCard: apiData.PANCard || null,
            Passbook: apiData.Passbook || null
          });
        } else {
          // Clear all inputs when data is null or ExitFormID doesn't exist
          setSectionAData({
            ExitFormID: 0,
            CustomerId: customerId,
            PranNumber: pranNumber,
            PranName: '',
            SubscriberName: '',
            OrganizationName: '',
            Subscribergendername: '',
            FatherName: '',
            MaritalStatusname: '',
            SpouseName: '',
            SpouseGendername: '',
            DOBPran: '',
            AadharNumber: '',
            PanCardNumber: '',
            MobileNo: '',
            AlternateMobileNo: '',
            EmailId: '',
            SubscriberAddress: ''
          });
          
          setSectionBData({
            BankAccountNumber: '',
            BankName: '',
            BankBranchAddress: '',
            IFSCCode: ''
          });
          
          setSectionCFormData({
            PRCorpusLumpsum: '',
            WorkSectorName: '',
            WorkSectorDOB: '',
            NPSCorpus: '',
            WithdrawalPR: '',
            WithdrawalAmount: '',
            AnnuityValue: '',
            AnnuitySchemeName: '',
            AnnuityFrequencySchemeName: ''
          });
          
          setSectionEData({
            nomineeE: []
          });
          
          setSectionFData({
            member: []
          });
          
          setSectionGData({
            AadharCardF: null,
            AadharCardB: null,
            PRANCard: null,
            PANCard: null,
            Passbook: null
          });
        }
      } else {
        // Clear all inputs when response is null
        setSectionAData({
          ExitFormID: 0,
          CustomerId: customerId,
          PranNumber: pranNumber,
          PranName: '',
          SubscriberName: '',
          OrganizationName: '',
          Subscribergendername: '',
          FatherName: '',
          MaritalStatusname: '',
          SpouseName: '',
          SpouseGendername: '',
          DOBPran: '',
          AadharNumber: '',
          PanCardNumber: '',
          MobileNo: '',
          AlternateMobileNo: '',
          EmailId: '',
          SubscriberAddress: ''
        });
        
        setSectionBData({
          BankAccountNumber: '',
          BankName: '',
          BankBranchAddress: '',
          IFSCCode: ''
        });
        
        setSectionCFormData({
          PRCorpusLumpsum: '',
          WorkSectorName: '',
          WorkSectorDOB: '',
          NPSCorpus: '',
          WithdrawalPR: '',
          WithdrawalAmount: '',
          AnnuityValue: '',
          AnnuitySchemeName: '',
          AnnuityFrequencySchemeName: ''
        });
        
        setSectionEData({
          nomineeE: []
        });
        
        setSectionFData({
          member: []
        });
        
        setSectionGData({
          AadharCardF: null,
          AadharCardB: null,
          PRANCard: null,
          PANCard: null,
          Passbook: null
        });
      }
    } catch (error) {
      // Error fetching exit form data - clear all inputs
      setSectionAData({
        ExitFormID: 0,
        CustomerId: customerId,
        PranNumber: pranNumber,
        PranName: '',
        SubscriberName: '',
        OrganizationName: '',
        Subscribergendername: '',
        FatherName: '',
        MaritalStatusname: '',
        SpouseName: '',
        SpouseGendername: '',
        DOBPran: '',
        AadharNumber: '',
        PanCardNumber: '',
        MobileNo: '',
        AlternateMobileNo: '',
        EmailId: '',
        SubscriberAddress: ''
      });
      
      setSectionBData({
        BankAccountNumber: '',
        BankName: '',
        BankBranchAddress: '',
        IFSCCode: ''
      });
      
      setSectionCFormData({
        PRCorpusLumpsum: '',
        WorkSectorName: '',
        WorkSectorDOB: '',
        NPSCorpus: '',
        WithdrawalPR: '',
        WithdrawalAmount: '',
        AnnuityValue: '',
        AnnuitySchemeName: '',
        AnnuityFrequencySchemeName: ''
      });
      
      setSectionEData({
        nomineeE: []
      });
      
      setSectionFData({
        member: []
      });
      
      setSectionGData({
        AadharCardF: null,
        AadharCardB: null,
        PRANCard: null,
        PANCard: null,
        Passbook: null
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="stepper-modal-container">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="loader-overlay">
          <div className="loader-spinner">
            <div className="spinner"></div>
            <p className="loader-text">Loading...</p>
          </div>
        </div>
      )}
      
      {/* Stepper Progress Indicator */}
      <div className="stepper-header">
        <ul className="stepper stepper-horizontal">
          {steps.map((step) => {
            const status = getStepStatus(step.number);
            return (
              <li
                key={step.number}
                className={`stepper-step ${status === 'completed' ? 'stepper-step-complete' : ''} ${status === 'active' ? 'stepper-step-active' : ''}`}
              >
                <div className="stepper-step-label">
                  <span className="stepper-step-number">
                    {status === 'completed' ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      step.number
                    )}
                  </span>
                  <span className="stepper-step-title">{step.title}</span>
                  <span className="stepper-step-subtitle">{step.subtitle}</span>
                </div>
                {step.number < totalSteps && (
                  <div className="stepper-step-line"></div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Scrollable Content Area */}
      <div className="stepper-content-scroll">
        <div className="stepper-content">
          {currentStep === 1 && (
            <SectionA 
              formData={sectionAData}
              onDataChange={setSectionAData} 
              onValidationChange={setIsSectionAValid}
              onPranNumberComplete={handlePranNumberComplete}
            />
          )}
          {currentStep === 2 && (
            <SectionB 
              formData={sectionBData} 
              onDataChange={setSectionBData}
              onValidationChange={setIsSectionBValid}
            />
          )}
          {currentStep === 3 && (
            <SectionC 
              formData={sectionCFormData}
              onDataChange={handleSectionCDataChange}
              onValidationChange={setIsSectionCValid}
            />
          )}
          {currentStep === 4 && (
            <SectionD 
              selectedBankData={sectionCData.selectedBank}
              annuityScheme={sectionCData.annuityScheme}
              annuityFrequency={sectionCData.annuityFrequency}
            />
          )}
          {currentStep === 5 && (
            <SectionE 
              formData={sectionEData}
              onDataChange={setSectionEData}
              onValidationChange={setIsSectionEValid} 
            />
          )}
          {currentStep === 6 && (
            <SectionF 
              formData={sectionFData}
              onDataChange={setSectionFData}
            />
          )}
          {currentStep === 7 && (
            <SectionG 
              formData={sectionGData}
              onDataChange={setSectionGData}
            />
          )}
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="stepper-footer">
        <div className="stepper-buttons">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleBack}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
          {currentStep < totalSteps ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !isSectionAValid) || 
                (currentStep === 2 && !isSectionBValid) ||
                (currentStep === 3 && !isSectionCValid) ||
                (currentStep === 5 && !isSectionEValid)
              }
              title={
                (currentStep === 1 && !isSectionAValid) 
                  ? 'Please fill all required fields correctly' 
                  : (currentStep === 2 && !isSectionBValid)
                  ? 'Please fill all required fields correctly'
                  : (currentStep === 3 && !isSectionCValid)
                  ? sectionCData.selectedBank 
                    ? 'Please fill all required fields correctly'
                    : 'Please select a bank to continue'
                  : (currentStep === 5 && !isSectionEValid) 
                  ? 'Please ensure total nominee percentage is 100%' 
                  : ''
              }
            >
              <span>Next</span>
              <i className="fas fa-arrow-right"></i>
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              <i className="fas fa-check"></i>
              <span>Submit</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepperComponent;

